import { useCameraResolutionPolicy, CameraResolutionPolicy } from '../hooks/useCameraResolutionPolicy';
import { useMeetingPresence } from '@/modules/meetings/components/MeetingReminderProvider';
import type { ApiError } from "@/api/api-axios";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { getLivekitTokenApi } from "@/modules/meetings/api/api-meeting";
import {
  Meeting,
  MeetingDevice,
  MeetingParticipant,
} from "@/modules/meetings/types/meeting.type";
import { Participant, Room, RoomEvent, Track } from "livekit-client";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  getMeetingByCodeApi,
  muteParticipantMeeting,
  remoteParticipantMeeting,
} from "../api/api-setting-meeting";

export interface LiveKitContextType {
  // State
  room: Room;
  participants: MeetingParticipant[];
  meetingDevices: MeetingDevice[];

  meeting: Meeting | null;
  cameraResolutionPolicy: CameraResolutionPolicy | null;
  currentUserRole: "host" | "participant";
  currentUserRaisedHand: boolean;
  currentUserCanShare: boolean;
  isHostOrCoHost: boolean;
  isActionsLocked: boolean;

  isConnecting: boolean;
  error: string | null;

  // Connection functions
  connectToMeeting: (
    meetingCode: string,
    userId: string,
    userName: string
  ) => Promise<void>;
  disconnect: () => Promise<void>;

  // Meeting control functions (for host/co_host)
  muteParticipant: (identity: string) => Promise<void>;
  unmuteParticipant: (identity: string) => Promise<void>;
  disableParticipantCamera: (identity: string) => Promise<void>;
  enableParticipantCamera: (identity: string) => Promise<void>;
  removeParticipant: (identity: string) => Promise<void>;
  setParticipantVolume: (identity: string, volume: number) => Promise<void>;

  // Helper functions
  getParticipantRole: (participant: Participant) => "host" | "participant";
  refreshMeeting: () => Promise<void>;
  updateParticipantsFromMqtt: (data: {
    userHands?: string[];
    userHosts?: string[];
    userShares?: string[];
  }) => void;
  /** Cập nhật lockAction từ bản tin MQTT 46 (khóa thao tác người dùng). */
  updateLockActionFromMqtt: (lockAction: boolean) => void;
}

const LiveKitContext = createContext<LiveKitContextType | undefined>(undefined);

export const useLiveKit = () => {
  const context = useContext(LiveKitContext);
  if (!context) {
    throw new Error("useLiveKit must be used within a LiveKitProvider");
  }
  return context;
};

interface LiveKitProviderProps {
  children: ReactNode;
}

export const LiveKitProvider: React.FC<LiveKitProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { setActiveMeetingCode } = useMeetingPresence();
  const [room] = useState<Room>(() => {
    return new Room({
      adaptiveStream: true,
      dynacast: true,
    });
  });
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const cameraResolutionPolicy = useCameraResolutionPolicy(room, meeting?.id);
  const meetingCodeRef = useRef<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [meetingDevices, setMeetingDevices] = useState<MeetingDevice[]>([]);

  const [currentUserRole, setCurrentUserRole] = useState<
    "host" | "participant"
  >("participant");

  // Tính toán trạng thái của user hiện tại
  const currentUserRaisedHand = useMemo(() => {
    if (!meeting ) return false;
    const currentParticipant = meeting.participants.find(
      (p) =>
        p.identity === currentUserId
    );
    return currentParticipant?.isRaiseHand ?? false;
  }, [meeting, currentUserId]);

  const currentUserCanShare = useMemo(() => {
    if (!meeting ) return true; // Mặc định là true nếu không có thông tin
    
    const currentParticipant = meeting.participants.find(
      (p) =>
        p.identity === currentUserId
    );
     const manager = meeting.createdBy === currentUserId || currentParticipant?.role === 'host' || currentParticipant?.isCoHost === true;
     return manager || (meeting.allowScreenShare !== false && (currentParticipant?.canShared ?? true));
  }, [meeting, currentUserId]);

  // Kiểm tra xem user hiện tại có phải là host hoặc co-host không
  const isHostOrCoHost = useMemo(() => {
    if (!meeting) return false;
    const currentParticipant = meeting.participants.find(
      (p) =>  p.identity === currentUserId
    );
    console.log("currentParticipant", currentParticipant);

    return (
      meeting.createdBy === currentUserId ||
      currentUserRole === "host" ||
      currentParticipant?.role === "host" ||
      currentParticipant?.isCoHost === true
    );
  }, [meeting, currentUserRole, currentUserId]);

  const isActionsLocked = useMemo(() => {
    if (!meeting?.lockAction) return false;
    return !isHostOrCoHost;
  }, [meeting?.lockAction, isHostOrCoHost]);

  useEffect(() => {
    if (!room) return;

    const handleConnected = () => {
      setIsConnecting(false);
      setError(null);
    };

    const handleDisconnected = () => {
      setActiveMeetingCode(code => code === meetingCodeRef.current ? null : code);
      setIsConnecting(false);
    };

    const handleReconnecting = () => {
      setIsConnecting(true);
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
    };
  }, [room]);

  const connectToMeeting = useCallback(
    async (meetingCode: string, userId: string, userName: string) => {
      if (!meetingCode) {
        setError("Meeting ID không hợp lệ");
        setIsConnecting(false);
        return;
      }

      if (!userName || !userName.trim()) {
        setError("Tên người tham gia không hợp lệ");
        setIsConnecting(false);
        return;
      }

      try {
        setIsConnecting(true);
        setError(null);

        const detailMeeting = await getMeetingByCodeApi(meetingCode);
        if (!detailMeeting) {
          throw new Error("Không thể lấy thông tin cuộc họp");
        }

        const meetingData = detailMeeting.data;
        setMeeting(meetingData);
        meetingCodeRef.current = meetingData.meetingCode;
        setCurrentUserId(userId);
        const { participants, devices } = meetingData;
        setParticipants(participants);
        setMeetingDevices(devices);

        let role: "host" | "participant" = "participant";

        if (isAuthenticated) {
          const participant = participants.find(
            (p: MeetingParticipant) => p.userId === userId
          );

          if (participant && participant.role) {
            const participantRole = participant.role as
              | "host"
              | "participant";
            if (["host", "participant"].includes(participantRole)) {
              role = participantRole;
              setCurrentUserRole(role);
            }
          }
        }

        const response = await getLivekitTokenApi({
          meetingCode: meetingCode,
          participantIdentity: userId,
          participantName: userName,
          role,
          metadata: {
            userId: userId,
            userName: userName,
            role: role,
          },
        });

        const { token, serverUrl: responseServerUrl } = response.data;

        if (!token) {
          setError("Không thể lấy token kết nối");
          setIsConnecting(false);
          return;
        }

        const finalServerUrl = responseServerUrl || "wss://dev-livekit.truyenthanhso.vn";

        await room.connect(finalServerUrl, token);
        setActiveMeetingCode(meetingCode);

        toast.success("Đã tham gia cuộc họp");
      } catch (err) {
        toast.error(`Be: ${(err as ApiError)?.data?.error?.message ?? ""}`);
        setIsConnecting(false);
        setError(`Be: ${(err as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, isAuthenticated, setActiveMeetingCode]
  );

  const disconnect = useCallback(async () => {
    if (!room) return;

    try {
      if (room.state === "connected") {
        await room.disconnect();
      }
      setActiveMeetingCode(code => code === meetingCodeRef.current ? null : code);
      setMeeting(null);
      meetingCodeRef.current = null;
      setCurrentUserId(null);
      setError(null);
    } catch (error) {
      throw error;
    }
  }, [room, setActiveMeetingCode]);


  const getParticipantRole = useCallback(
    (participant: Participant): "host" | "participant" => {
      if (!meeting) return "participant";
      const meetingParticipant = meeting.participants.find(
        (p) => p.userId === participant.identity
      );
      return (
        (meetingParticipant?.role as "host" | "participant") ?? "participant"
      );
    },
    [meeting]
  );

  const refreshMeeting = useCallback(async () => {
    const meetingCode = meetingCodeRef.current;

    if (!meetingCode) {
      return;
    }

    try {
      const detailMeeting = await getMeetingByCodeApi(meetingCode);
      if (!detailMeeting?.data) {
        return;
      }

      const meetingData = detailMeeting.data;
      setMeeting(meetingData);
      meetingCodeRef.current = meetingData.meetingCode;

      const { participants, devices } = meetingData;
      setParticipants(participants);
      setMeetingDevices(devices);
    } catch (error) {
      toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
    }
  }, []);

  useEffect(() => {
    room.on(RoomEvent.Reconnected, refreshMeeting);
    const timer = window.setInterval(() => { if (room.state === "connected") void refreshMeeting(); }, 15000);
    return () => { room.off(RoomEvent.Reconnected, refreshMeeting); window.clearInterval(timer); };
  }, [room, refreshMeeting]);

  const muteParticipant = useCallback(
    async (identity: string) => {
      if (!room || !meeting) {
        toast.error("Bạn không có quyền thực hiện thao tác này");
        return;
      }

      try {
        await muteParticipantMeeting(meeting.id, {
          participantIdentity: identity,
          trackType: Track.Kind.Audio,
          muted: true,
        });
        const data = new Uint8Array([1]);
        await room.localParticipant.publishData(data, {
          reliable: true,
          destinationIdentities: [identity],
        });
        toast.success(`Đã tắt mic của ${identity}`);
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, meeting]
  );

  const unmuteParticipant = useCallback(
    async (identity: string) => {
      if (!room || !meeting) {
        toast.error("Bạn không có quyền thực hiện thao tác này");
        return;
      }

      try {
        await muteParticipantMeeting(meeting.id, {
          participantIdentity: identity,
          trackType: Track.Kind.Audio,
          muted: false,
        });
        const data = new Uint8Array([0]);
        await room.localParticipant.publishData(data, {
          reliable: true,
          destinationIdentities: [identity],
        });
        toast.success(`Đã bật mic của ${identity}`);
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, meeting]
  );

  const disableParticipantCamera = useCallback(
    async (identity: string) => {
      if (!room || !meeting) {
        toast.error("Bạn không có quyền thực hiện thao tác này");
        return;
      }

      try {
        await muteParticipantMeeting(meeting.id, {
          participantIdentity: identity,
          trackType: Track.Kind.Video,
          muted: true,
        });

        const data = new Uint8Array([2]);
        await room.localParticipant.publishData(data, {
          reliable: true,
          destinationIdentities: [identity],
        });
        toast.success(`Đã tắt camera của ${identity}`);
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, meeting]
  );

  const enableParticipantCamera = useCallback(
    async (identity: string) => {
      if (!room || !meeting) {
        toast.error("Bạn không có quyền thực hiện thao tác này");
        return;
      }

      try {
        await muteParticipantMeeting(meeting.id, {
          participantIdentity: identity,
          trackType: Track.Kind.Video,
          muted: false,
        });
        const data = new Uint8Array([3]);
        await room.localParticipant.publishData(data, {
          reliable: true,
          destinationIdentities: [identity],
        });
        toast.success(`Đã bật camera của ${identity}`);
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, meeting]
  );

  const removeParticipant = useCallback(
    async (identity: string) => {
      if (!room || !meeting) {
        toast.error("Bạn không có quyền thực hiện thao tác này");
        return;
      }

      try {
        await remoteParticipantMeeting(meeting.id, {
          participantIdentity: identity,
        });
        const data = new Uint8Array([4]);
        await room.localParticipant.publishData(data, {
          reliable: true,
          destinationIdentities: [identity],
        });
        toast.success(`Đã xóa ${identity} khỏi cuộc họp`);
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room, meeting]
  );

  const setParticipantVolume = useCallback(
    async (identity: string, volume: number) => {
      if (!room) return;

      try {
        const track = room.remoteParticipants
          .get(identity)
          ?.getTrackPublication(Track.Source.Microphone);
        if (track && track.track) {
          if (track.track instanceof HTMLAudioElement) {
            track.track.volume = volume / 100;
          } else if (track.track.mediaStreamTrack) {
            console.log(`Setting volume for ${identity} to ${volume}%`);
          }
        }
      } catch (error) {
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [room]
  );

  const updateLockActionFromMqtt = useCallback((lockAction: boolean) => {
    setMeeting((prevMeeting) => {
      if (!prevMeeting) return prevMeeting;
      return { ...prevMeeting, lockAction };
    });
  }, []);

  // Function để cập nhật participants từ MQTT message
  const updateParticipantsFromMqtt = useCallback(
    (data: {
      userHands?: string[];
      userHosts?: string[];
      userShares?: string[];
    }) => {
      if (!meeting) return;

      setMeeting((prevMeeting) => {
        if (!prevMeeting) return prevMeeting;

        const updatedParticipants = prevMeeting.participants.map((participant) => {
          const identity = participant.identity || participant.userId;
          
          // Cập nhật isRaiseHand từ UserHands
          // Nếu có data.userHands, chỉ những người trong danh sách mới giơ tay
          const isRaiseHand = data.userHands 
            ? data.userHands.includes(identity)
            : (participant.isRaiseHand ?? false);
          
          // Cập nhật isCoHost từ UserHosts
          // Nếu participant là host thì không phải co-host
          // Nếu có data.userHosts, chỉ những người trong danh sách (và không phải host) mới là co-host
          const isCoHost = participant.role === "host" 
            ? false 
            : (data.userHosts 
              ? data.userHosts.includes(identity)
              : (participant.isCoHost ?? false));
          
          // Cập nhật canShared từ UserShares
          // Nếu có data.userShares, chỉ những người trong danh sách mới được phép chia sẻ
          const canShared = data.userShares 
            ? data.userShares.includes(identity)
            : (participant.canShared ?? true);

          return {
            ...participant,
            isRaiseHand,
            isCoHost,
            canShared,
          };
        });

        return {
          ...prevMeeting,
          participants: updatedParticipants,
        };
      });
    },
    [meeting]
  );

  useEffect(() => {
    return () => {
      if (room && room.state === "connected") {
        room.disconnect();
      }
    };
  }, [room]);

  const value = useMemo<LiveKitContextType>(
    () => ({
      room,
      cameraResolutionPolicy,
      participants,
      meetingDevices,
      meeting,
      currentUserRole,
      currentUserRaisedHand,
      currentUserCanShare,
      isHostOrCoHost,
      isActionsLocked,
      isConnecting,
      error,
      connectToMeeting,
      disconnect,
      muteParticipant,
      unmuteParticipant,
      disableParticipantCamera,
      enableParticipantCamera,
      removeParticipant,
      setParticipantVolume,
      getParticipantRole,
      refreshMeeting,
      updateParticipantsFromMqtt,
      updateLockActionFromMqtt,
    }),
    [
      room,
      cameraResolutionPolicy,
      participants,
      meetingDevices,
      meeting,
      currentUserRole,
      currentUserRaisedHand,
      currentUserCanShare,
      isHostOrCoHost,
      isActionsLocked,
      isConnecting,
      error,
      connectToMeeting,
      disconnect,
      muteParticipant,
      unmuteParticipant,
      disableParticipantCamera,
      enableParticipantCamera,
      removeParticipant,
      setParticipantVolume,
      getParticipantRole,
      refreshMeeting,
      updateParticipantsFromMqtt,
      updateLockActionFromMqtt,
    ]
  );

  return (
    <LiveKitContext.Provider value={value}>{children}</LiveKitContext.Provider>
  );
};
